import { ListPaymentsRequest, Payment } from "@breeztech/breez-sdk-spark/web";
import { getBreezSdk, getIdentityPubkey } from "./breezSdk";
import { PaymentMethod, PaymentType, sparkResponseToTransaction, Transaction, TransactionStatus, WalletType } from "../models/Transactions";
import { walletRepository } from "../repositories/WalletRepository";
import { mempoolRepository } from "../repositories/MempoolRepository";
import { getStoredOnchainAddresses, storeUsedOnchainAddress } from "./onchainAddressStorage";

export function getTransactionIdFromSparkPayment(
    sparkResponse: Payment,
): string {
    let transactionId = sparkResponse.id;
  
    if (sparkResponse.details?.type === 'lightning') {
      transactionId =
        sparkResponse.details?.htlcDetails?.paymentHash ?? sparkResponse.id;
    } else if (sparkResponse.details?.type === 'spark') {
      transactionId =
        sparkResponse.details?.htlcDetails?.paymentHash ?? sparkResponse.id;
    }
  
    return transactionId;
}

export async function doesPaymentExists(id: string): Promise<boolean> {
    const breezSdk = getBreezSdk();
    if(!breezSdk) {
        throw new Error('Breez SDK not found');
    }
    try {
        await breezSdk.getPayment({ paymentId: id });
        return true;
    } catch (error) {
        return false;
    }
}

export async function getSparkPayment(id: string, maxRetries: number = 0): Promise<Transaction | null> {
    const breezSdk = getBreezSdk();
    if(!breezSdk) {
        throw new Error('Breez SDK not found');
    }
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const retryDelayMs = 1000;
    const breezPayment = await breezSdk.getPayment({ paymentId: id });

    if(!breezPayment) {
        return null;
    }

    const transactionId = getTransactionIdFromSparkPayment(breezPayment.payment);

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            const serverTransaction = await walletRepository.getSparkPayment(transactionId);
            if(serverTransaction) {
                return serverTransaction;
            }
        } catch (error) {
            if (attempt === maxRetries) {
                console.error('Error fetching transaction from server', error);
            }
        }

        if (attempt < maxRetries) {
            await sleep(retryDelayMs);
        }
    }

    const transaction = sparkResponseToTransaction(breezPayment);
    return syncSparkPaymentWithServerIfNeeded(transaction, transactionId);
}

export async function syncSparkPaymentWithServerIfNeeded(transaction: Transaction, transactionId: string): Promise<Transaction | null> {
    if (transaction.status === TransactionStatus.SUCCESS && transaction.amount > 0) {
        try {
            const receivedTransaction = await walletRepository.sparkPaymentReceived({
                toIdentityPubkey: getIdentityPubkey(),
                paymentId: transaction.id,
                paymentHash: transactionId,
                amount: transaction.amount,
                fees: transaction.fee,
                comment: transaction.comment,
                timestamp: transaction.time / 1000,
                btcTxId: transaction.btcTxId,
                paymentMethod: transaction.invoice ? PaymentMethod.INVOICE : null,
            })
            if(receivedTransaction) {
                return receivedTransaction;
            }
        } catch (error) {
            console.error('Error reporting payment received', error);
        }
    }

    return transaction;
}

export async function listLocalBreezPayments(request: ListPaymentsRequest): Promise<Payment[]> {
    const breezSdk = getBreezSdk();
    if(!breezSdk) {
        throw new Error('Breez SDK not found');
    }
    const breezListPaymentsResponse = await breezSdk.listPayments({
        ...request,
        assetFilter: request.assetFilter ?? { type: 'bitcoin' },
    });

    const paymentsFromBreez = breezListPaymentsResponse.payments ?? [];

    const payments = paymentsFromBreez
        .map((payment: Payment) => {
            return {
                ...payment,
                id: getTransactionIdFromSparkPayment(payment),
            }
        });

    return payments;
}

export async function getPaymentsFromServer(payments: Payment[]): Promise<Transaction[]> {
    if (payments.length === 0) {
        return [];
    }

    const paymentIds = payments.map((payment) => payment.id);

    let serverTransactions: Transaction[] = [];
    try {
        serverTransactions = await walletRepository.getSparkPayments(paymentIds);
    } catch (error) {
        console.error('Error fetching transactions from server', error);
        serverTransactions = [];
    }

    const serverTxById = new Map<string, Transaction>(
        serverTransactions.map((tx) => [tx.id, tx])
    );

    return Promise.all(
        payments.map(async (payment) => {
            const serverTx = serverTxById.get(payment.id);
            if (serverTx) {
                return serverTx;
            }
            const transaction = sparkResponseToTransaction(payment);
            const synced = await syncSparkPaymentWithServerIfNeeded(transaction, payment.id);
            return synced ?? transaction;
        })
    );
}

export async function listBreezPaymentsWithDatas(request: ListPaymentsRequest): Promise<Transaction[]> {
    const payments = await listLocalBreezPayments(request);

    return await getPaymentsFromServer(payments);
}

export async function getBridgePendingDeposits(): Promise<Transaction[]> {
    const breezSdk = getBreezSdk();
    if(!breezSdk) {
        throw new Error('Breez SDK not found');
    }

    const { deposits } = await breezSdk.listUnclaimedDeposits({});

    return deposits
        .filter((deposit) => !deposit.isMature)
        .map((deposit) => {
            const id = `deposit:${deposit.txid}:${deposit.vout}`;
            return {
                id: id,
                paymentHash: id,
                status: TransactionStatus.PENDING,
                walletType: WalletType.SPARK,
                amountMsat: deposit.amountSats * 1000,
                amount: deposit.amountSats,
                fee: 0,
                comment: '',
                time: Date.now(),
                read: true,
                announced: true,
                type: PaymentType.individual,
                zapType: 'Zap',
                clientSideTransaction: true,
                customTitle: 'Pending deposit',
                btcTxId: deposit.txid,
                hidden: false,
            };
        });
}

export async function getMempoolPendingDeposits(): Promise<Transaction[]> {
    const walletId = getIdentityPubkey()?.trim();
    if (!walletId) {
        return [];
    }

    const normalizedAddresses = getStoredOnchainAddresses(walletId);
    if (normalizedAddresses.length === 0) {
        return [];
    }

    const addressSet = new Set(normalizedAddresses);

    const transactionsPerAddress = await Promise.all(
        normalizedAddresses.map(async (address) => {
            try {
                return await mempoolRepository.getMempoolAddressTransactions(address);
            } catch (error) {
                console.error(`getMempoolPendingDeposits failed for address=${address}`, error);
                return [];
            }
        })
    );
    const transactions = transactionsPerAddress.flat();

    const addressesWithTransactions = Array.from(
        new Set(
            transactions.flatMap((transaction) =>
                transaction.vout
                    .map((output) => output.scriptpubkeyAddress?.trim())
                    .filter((address): address is string => !!address && addressSet.has(address))
            )
        )
    );

    addressesWithTransactions.forEach((address) => {
        storeUsedOnchainAddress(walletId, address);
    });

    const pendingDeposits = transactions
        .filter((transaction) => !transaction.status.confirmed)
        .flatMap((transaction) =>
            transaction.vout.flatMap((output, vout) => {
                const address = output.scriptpubkeyAddress?.trim();
                if (!address || !addressSet.has(address)) {
                    return [];
                }
                if (output.value <= 0) {
                    return [];
                }

                const id = `mempool:${transaction.txid}:${vout}`;
                const transactionResult: Transaction = {
                    id: id,
                    paymentHash: id,
                    status: TransactionStatus.PENDING,
                    walletType: WalletType.SPARK,
                    amountMsat: output.value * 1000,
                    amount: output.value,
                    fee: 0,
                    comment: '',
                    time: Date.now(),
                    read: true,
                    announced: true,
                    type: PaymentType.individual,
                    zapType: 'Zap',
                    clientSideTransaction: true,
                    customTitle: 'Pending deposit',
                    btcTxId: transaction.txid,
                    hidden: false,
                };
                return [transactionResult];
            })
        );

    const uniqueById = new Map<string, Transaction>();
    pendingDeposits.forEach((transaction) => {
        if (!uniqueById.has(transaction.id)) {
            uniqueById.set(transaction.id, transaction);
        }
    });

    return Array.from(uniqueById.values());
}

function mergeTransactions(primary: Transaction[], secondary: Transaction[]): Transaction[] {
    const merged: Transaction[] = [];
    const seenIds = new Set<string>();

    [...primary, ...secondary].forEach((transaction) => {
        if (seenIds.has(transaction.id)) {
            return;
        }
        seenIds.add(transaction.id);
        merged.push(transaction);
    });

    return merged;
}

export async function getPendingDeposits(): Promise<Transaction[]> {
    const [bridgePendingDeposits, mempoolPendingDeposits] = await Promise.all([
        getBridgePendingDeposits(),
        getMempoolPendingDeposits(),
    ]);

    // Bridge deposits take priority over mempool deposits for the same on-chain transaction.
    return mergeTransactions(bridgePendingDeposits, mempoolPendingDeposits);
}
